/**
 * Client for the plugin's REST upload endpoint. Handles the stale-nonce case
 * (full-page caches serve product pages with expired `wp_rest` nonces) by
 * fetching a fresh nonce once and retrying.
 */

import type { MokkoningData } from './config';

export interface UploadResult {
    token: string;
    url: string;
}

export async function uploadPrintFile(
    data: MokkoningData,
    blob: Blob,
    design: Record<string, unknown>,
): Promise<UploadResult> {
    const attempt = (nonce: string): Promise<Response> => {
        const form = new FormData();
        form.append('file', blob, 'design.png');
        form.append('product_id', String(data.productId));
        form.append('design', JSON.stringify(design));
        return fetch(data.restUrl, {
            method: 'POST',
            headers: { 'X-WP-Nonce': nonce },
            body: form,
            credentials: 'same-origin',
        });
    };

    let response = await attempt(data.nonce);

    if (response.status === 403) {
        try {
            const session = await fetch(data.sessionUrl, { credentials: 'same-origin' });
            if (session.ok) {
                const { nonce } = (await session.json()) as { nonce: string };
                data.nonce = nonce;
                response = await attempt(nonce);
            }
        } catch {
            // fall through to the error below with the original response
        }
    }

    if (!response.ok) {
        let message = '';
        try {
            message = ((await response.json()) as { message?: string }).message ?? '';
        } catch {
            // non-JSON error body
        }
        throw new Error(message || `Upload failed (HTTP ${response.status})`);
    }

    return (await response.json()) as UploadResult;
}
